import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import type { PropsWithChildren } from 'react'

import { fapiConfigurationService } from '@/services/fapi'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import {
  selectCartData,
  selectIsAvoidProceedingCart,
  selectIsDisplayingDisableBProgramCheckbox,
} from '@/store/selectors/cartSelector'
import { getCartItems } from '@/store/slices/cartSlice'
import {
  selectBillingAddress,
  selectCustomerAddress,
  selectDeliveryAddresses,
} from '@/store/slices/customerSlice'
import { useAsync } from '@/hooks'
import { CONFIG_DATA_TYPES, CONFIG_NAMES } from '@/libs/constants/configs'
import { isValidArray } from '@/libs/utils/array'
import { linkGenerator } from '@/libs/utils/language'

import { CheckoutContext } from './useCheckoutContext'

async function getCheckoutConfig() {
  const CHECKOUT_CONFIGS = [
    {
      configurationKey: CONFIG_NAMES.SHOW_SERVICE_CODE,
      dataTypeDto: CONFIG_DATA_TYPES.BOOLEAN,
    },
    {
      configurationKey: CONFIG_NAMES.ORDER_REFERENCE_MANDATORY,
      dataTypeDto: CONFIG_DATA_TYPES.BOOLEAN,
    },
    {
      configurationKey: CONFIG_NAMES.SHIPPING_NOTES_MANDATORY,
      dataTypeDto: CONFIG_DATA_TYPES.BOOLEAN,
    },
  ]

  const response = await fapiConfigurationService.getConfigsByNames(CHECKOUT_CONFIGS)

  return response
}

export function CheckoutProvider({ children }: PropsWithChildren) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const customerInfo = useAppSelector(selectCustomerAddress)
  const addresses = useAppSelector(selectDeliveryAddresses)
  const billingInfo = useAppSelector(selectBillingAddress)
  const cartData = useAppSelector(selectCartData)
  const isAvoidProceedingCart = useAppSelector(selectIsAvoidProceedingCart)
  const isBProgramCheckboxDisplayed = useAppSelector(
    selectIsDisplayingDisableBProgramCheckbox,
  )

  const [shouldDisableCheckout, setDisableCheckout] = useState(
    isBProgramCheckboxDisplayed,
  )
  const { value: checkoutConfig } = useAsync(getCheckoutConfig, true)

  const {
    Show_Service_Code: shouldShowServiceCode = false,
    Order_reference_mandatory: isOrderReferenceMandatory = false,
    Shipping_notes_mandatory: isShippingNotesMandatory = false,
  } = checkoutConfig || {}

  const deliveryAddresses = useMemo(() => {
    if (addresses.length > 0) {
      return addresses
    }

    return customerInfo ? [customerInfo] : []
  }, [customerInfo, addresses])

  const memoizedCartData = useMemo(() => {
    if (isValidArray(cartData?.items)) {
      return cartData
    }

    return null
  }, [cartData])

  const handleDisableCheckout = useCallback(
    (value: React.SetStateAction<boolean>) => {
      setDisableCheckout(value)
    },
    [],
  )

  const memoizedValue = useMemo(
    () => ({
      customerInfo,
      billingInfo,
      cartData: memoizedCartData,
      shouldShowServiceCode,
      isOrderReferenceMandatory,
      isShippingNotesMandatory,
      shouldDisableCheckout,
      setDisableCheckout: handleDisableCheckout,
      deliveryAddresses,
    }),
    [
      customerInfo,
      billingInfo,
      memoizedCartData,
      shouldShowServiceCode,
      isOrderReferenceMandatory,
      isShippingNotesMandatory,
      shouldDisableCheckout,
      handleDisableCheckout,
      deliveryAddresses,
    ],
  )

  useEffect(() => {
    dispatch(getCartItems())
  }, [dispatch])

  useEffect(() => {
    if (isAvoidProceedingCart) {
      navigate(linkGenerator('/cart'), { replace: true })
    }
  }, [isAvoidProceedingCart, navigate])

  useEffect(() => {
    setDisableCheckout(isBProgramCheckboxDisplayed)
  }, [isBProgramCheckboxDisplayed])

  return <CheckoutContext value={memoizedValue}>{children}</CheckoutContext>
}
